import type { ReactElement } from 'react'
import styled, { keyframes } from 'styled-components'

import type { CanvasRect } from './graphInstrumentation'
import { IsolationBadge } from './IsolationBadge'
import type { IsolationHighlight, IsolationMarker } from './isolationMarkers'

const OUTLINE_PADDING = 4
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'

const pulse = keyframes`
  50% { stroke-width: 7; }
`
const Outline = styled.rect`
  fill: var(--pnid-isolation-point);
  fill-opacity: 0.14;
  stroke: var(--pnid-isolation-point);
  stroke-width: 2;
  vector-effect: non-scaling-stroke;
  &[data-focused='true'] {
    stroke-width: 3.5;
  }
  &[data-pulse='true'] {
    animation: ${pulse} 500ms ease-in-out 3;
  }
`

const HighlightOutline = styled.rect`
  fill: var(--pnid-target-equipment);
  fill-opacity: 0.35;
  stroke: var(--pnid-target-equipment);
  stroke-width: 4;
  vector-effect: non-scaling-stroke;
`

type OutlineBox = Pick<MarkerShape, 'x' | 'y' | 'width' | 'height'>

type MarkerShape = {
  key: string
  nodeId: string
  badge: string
  focused: boolean
  x: number
  y: number
  width: number
  height: number
}

function prefersReducedMotion(): boolean {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia(REDUCED_MOTION_QUERY).matches
  )
}

function outlineBox(bounds: CanvasRect): OutlineBox {
  return {
    x: bounds.denormalized_x - OUTLINE_PADDING,
    y: bounds.denormalized_y - OUTLINE_PADDING,
    width: bounds.denormalized_width + OUTLINE_PADDING * 2,
    height: bounds.denormalized_height + OUTLINE_PADDING * 2,
  }
}

function markerShape({
  overlayId,
  nodeId,
  badge,
  bounds,
  focus,
}: IsolationMarker): MarkerShape {
  return {
    key: `${overlayId}:${focus ?? ''}`,
    nodeId,
    badge,
    focused: focus !== undefined,
    ...outlineBox(bounds),
  }
}

function focusedLast(left: MarkerShape, right: MarkerShape): number {
  return Number(left.focused) - Number(right.focused)
}

export function IsolationMarkerLayer({
  markers,
  highlights,
}: {
  markers: IsolationMarker[]
  highlights: IsolationHighlight[]
}): ReactElement {
  const shapes = markers.map(markerShape).sort(focusedLast)
  const animate = !prefersReducedMotion()
  return (
    <g data-isolation-markers>
      {highlights.map(({ overlayId, nodeId, bounds }) => (
        <HighlightOutline
          key={overlayId}
          data-isolation-highlight={nodeId}
          {...outlineBox(bounds)}
          rx={2}
        />
      ))}
      {shapes.map(({ key, nodeId, focused, x, y, width, height }) => (
        <Outline
          key={key}
          data-isolation-marker={nodeId}
          data-focused={focused}
          data-pulse={focused && animate}
          x={x}
          y={y}
          width={width}
          height={height}
          rx={2}
        />
      ))}
      {shapes.map(({ key, badge, x, y, width }) => (
        <IsolationBadge key={key} x={x + width} y={y} label={badge} />
      ))}
    </g>
  )
}
